<script setup lang="ts">
import { useToast } from 'primevue/usetoast'
import { onMounted, ref, computed } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import type ExternalBankDetails from '@/api/models/ExternalBank/ExternalBankDetails'
import type GetBanksReq from '@/api/models/ExternalBank/GetBankReq'
import ApiService from '@/api/ApiService'
import type { ToastMessageOptions } from 'primevue/toast'
import NotFound from '@/assets/not_found.svg'
import ConversionUtility from '../utils/ConversionUtility'
import LoadingComponent from '@/components/LoadingComponent.vue'
import CustomButton from '@/components/CustomComponent/CustomButton.vue'
import type AddBankReq from '@/api/models/ExternalBank/AddBankReq'
import type { CreatedBy } from '@/api/models/CreatedBy'
import Storage from '@/utils/Storage'
import type DepositReq from '@/api/models/ExternalBank/DepositReq'
import type WithdrawReq from '@/api/models/ExternalBank/WithdrawReq'
import ValidationUtility from '@/utils/ValidationUtility'

const toast = useToast()
const isLoading = ref(false)
const confirmDialog = useConfirm()
const addNewBank = ref(false)
const searchQuery = ref('')

const bankName = ref('')
const accountNo = ref('')

const existingBanks = ref<ExternalBankDetails[]>([])
const filteredBanks = computed(() => {
  if (!searchQuery.value) return existingBanks.value
  
  const query = searchQuery.value.toLowerCase()
  return existingBanks.value.filter(bank => 
    bank.bankName.toLowerCase().includes(query) || 
    bank.accountNumber.toLowerCase().includes(query)
  )
})

const selectedBank = ref<ExternalBankDetails>()
const viewDialog = ref(false)
const action = ref('')
const amount = ref(0)
const transDate = ref<Date>(new Date())
const description = ref('')
const maxWithdrawAmount = computed(() => {
  if (!selectedBank.value) return 0
  return ConversionUtility.toRuppesCoversion(selectedBank.value.balance)
})

const isWithdrawValid = computed(() => {
  if (action.value !== 'Withdraw') return true
  return amount.value > 0 && amount.value < maxWithdrawAmount.value
})

// For DataTable
const tableColumns = ref([
  { field: 'bankName', header: 'Bank Name', sortable: true },
  { field: 'accountNumber', header: 'Account No', sortable: true },
  { field: 'balance', header: 'Balance', sortable: true, body: 'balanceTemplate' },
  { field: 'createdBy.createdAt', header: 'Created on', sortable: true, body: 'dateTemplate' },
  { field: 'actions', header: 'Actions', body: 'actionsTemplate' }
])

onMounted(() => {
  handleGetBanks()
})

function handleGetBanks() {
  isLoading.value = true
  const req: GetBanksReq = {
    query: ''
  }
  ApiService.getBanks(req)
    .then((response) => {
      if (response.statusCode == 200) {
        existingBanks.value = response.data
      }
    })
    .catch((error) => {
      showToast('Failed to fetch Banks', error, 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function addBank() {
  if (!bankName.value) {
    showToast('Error', 'Please enter Bank Name', 'error')
    return
  }
  if (!accountNo.value) {
    showToast('Error', 'Please enter account No', 'error')
    return
  }

  if (!ValidationUtility.validateNumber(accountNo.value)) {
    showToast('Error', 'Please enter only numbers as account No', 'error')
    return
  }

  isLoading.value = true
  const createdBy: CreatedBy = {
    empId: Storage.getEmpId()!,
    createdAt: new Date().getTime()
  }
  const req: AddBankReq = {
    bankName: bankName.value.trim().toString().toUpperCase(),
    accountNumber: accountNo.value.toString(),
    createdBy: createdBy
  }
  ApiService.addExternalBank(req)
    .then((response) => {
      if (response.statusCode == 200) {
        showToast('Success', 'Bank Added Successfully', 'success')
        addNewBank.value = false
        bankName.value = ''
        accountNo.value = ''
        handleGetBanks()
      } else {
        showToast('Error', response.error!, 'error')
      }
    })
    .catch((error) => {
      showToast('Error', 'Something went wrong', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function handleDepositClick(bank: ExternalBankDetails) {
  selectedBank.value = bank
  viewDialog.value = true
  transDate.value = new Date()
  action.value = 'Deposit'
  amount.value = 0
  description.value = ''
}

function handleWithdrawClick(bank: ExternalBankDetails) {
  selectedBank.value = bank
  viewDialog.value = true
  transDate.value = new Date()
  action.value = 'Withdraw'
  amount.value = 0
  description.value = ''
}

function performAction() {
  if (!transDate.value) {
    showToast('Error', 'Please enter Transaction Date', 'error')
    return
  }
  if (!amount.value) {
    showToast('Error', 'Please enter amount', 'error')
    return
  }
  
  // Confirmation dialog for transaction
  const confirmTitle = action.value === 'Deposit' ? 'Confirm Deposit' : 'Confirm Withdrawal'
  const confirmMessage = action.value === 'Deposit' 
    ? `Are you sure you want to deposit ₹${amount.value.toLocaleString('en-IN')} to ${selectedBank.value?.bankName}?`
    : `Are you sure you want to withdraw ₹${amount.value.toLocaleString('en-IN')} from ${selectedBank.value?.bankName}?`
  
  confirmDialog.require({
    message: confirmMessage,
    header: confirmTitle,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: action.value === 'Deposit' ? 'Yes, Deposit' : 'Yes, Withdraw',
    rejectLabel: 'Cancel',
    accept: () => {
      if (action.value == 'Deposit') {
        processDeposit()
      } else {
        processWithdrawal()
      }
    }
  })
}

function processDeposit() {
  const req: DepositReq = {
    bankName: selectedBank.value?.bankName!,
    deposit: {
      amount: ConversionUtility.toPaiseConversion(amount.value),
      createdAt: new Date(transDate.value).getTime(),
      desc: description.value || `Deposit to ${selectedBank.value?.bankName}`
    }
  }
  isLoading.value = true

  ApiService.addExternalBankDeposit(req)
    .then((response) => {
      if (response.statusCode == 200) {
        showToast(
          'Success',
          `₹${amount.value.toLocaleString('en-IN')} deposited to ${selectedBank.value?.bankName}`,
          'success'
        )
        viewDialog.value = false
        action.value = ''
        transDate.value = new Date()
        amount.value = 0
        description.value = ''
        handleGetBanks()
      } else {
        showToast('Error', response.error!, 'error')
      }
    })
    .catch((error) => {
      showToast('Error', 'Something went wrong', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function processWithdrawal() {
  if(amount.value > maxWithdrawAmount.value) {
    showToast('Error', 'Withdraw amount should be less than balance', 'error')
    return
  }
  
  if(amount.value === maxWithdrawAmount.value) {
    showToast('Error', 'Cannot withdraw the entire balance, minimum balance required', 'error')
    return
  }
  
  const req: WithdrawReq = {
    bankName: selectedBank.value?.bankName!,
    withdraw: {
      amount: ConversionUtility.toPaiseConversion(amount.value),
      createdAt: new Date(transDate.value).getTime(),
      desc: description.value || `Withdraw from ${selectedBank.value?.bankName}`
    }
  }
  isLoading.value = true

  ApiService.addExternalBankWithdraw(req)
    .then((response) => {
      if (response.statusCode == 200) {
        showToast(
          'Success',
          `₹${amount.value.toLocaleString('en-IN')} withdrawn from ${selectedBank.value?.bankName}`,
          'success'
        )
        viewDialog.value = false
        action.value = ''
        transDate.value = new Date()
        amount.value = 0
        description.value = ''
        handleGetBanks()
      } else {
        showToast('Error', response.error!, 'error')
      }
    })
    .catch((error) => {
      showToast('Error', 'Something went wrong', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function handleAddClick() {
  addNewBank.value = true
  bankName.value = ''
  accountNo.value = ''
}

function showToast(summary: string, detail: string, severity: ToastMessageOptions['severity']) {
  toast.add({
    severity: severity,
    summary: summary,
    detail: detail,
    life: 5000
  })
}
</script>
<template>
  <Toast class="error-p-toast" />
  <main class="main" v-bind="$attrs">
    <Card class="external-banks-card">
      <template #title>
        <div class="card-header">
          <h2 class="text-2xl font-bold">External Banks</h2>
          <div class="search-container">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText v-model="searchQuery" placeholder="Search banks..." aria-label="Search banks" />
            </span>
          </div>
        </div>
      </template>
      <template #content>
        <div v-if="!existingBanks.length && !isLoading" class="empty-state">
          <Image :src="NotFound" alt="No banks found" width="300" />
          <h3 class="mt-4 text-xl">No External Banks Found</h3>
          <p class="text-gray-500 mb-4">Add your first external bank to get started</p>
          <Button label="Add New Bank" icon="pi pi-plus" @click="handleAddClick" />
        </div>
        <div v-else class="data-table-container">
          <DataTable 
            v-if="existingBanks.length" 
            :value="filteredBanks" 
            stripedRows 
            paginator 
            :rows="10" 
            :rowsPerPageOptions="[5,10,25]"
            tableStyle="min-width: 50rem"
            class="external-banks-table"
            dataKey="accountNumber"
            :sortField="tableColumns[0].field"
            :sortOrder="1"
            responsiveLayout="scroll"
            rowHover
            showGridlines
          >
            <Column field="bankName" header="Bank Name" sortable>
              <template #body="slotProps">
                <div class="bank-name-cell">
                  <i class="pi pi-building mr-2"></i>
                  <span>{{ slotProps.data.bankName }}</span>
                </div>
              </template>
            </Column>
            
            <Column field="accountNumber" header="Account No" sortable>
              <template #body="slotProps">
                <span class="account-number">{{ slotProps.data.accountNumber }}</span>
              </template>
            </Column>
            
            <Column field="balance" header="Balance" sortable>
              <template #body="slotProps">
                <span class="balance-amount">
                  {{ ConversionUtility.toStringRupeesConversion(slotProps.data.balance) }}
                </span>
              </template>
            </Column>
            
            <Column field="createdBy.createdAt" header="Created on" sortable>
              <template #body="slotProps">
                <div class="date-cell">
                  <i class="pi pi-calendar mr-2"></i>
                  <span>{{ ConversionUtility.toDateFormat(slotProps.data.createdBy.createdAt) }}</span>
                </div>
              </template>
            </Column>
            
            <Column header="Actions" style="min-width: 15rem; text-align: left;">
              <template #body="slotProps">
                <div class="action-buttons" style="justify-content: flex-start;">
                  <CustomButton
                    title="Deposit"
                    icon="plus"
                    @click="handleDepositClick(slotProps.data)"
                  />
                   <CustomButton
                    title="Withdraw"
                    icon="minus"
                    severity="danger"
                    @click="handleWithdrawClick(slotProps.data)"
                  />
                </div>
              </template>
            </Column>
            
            <template #empty>
              <div class="text-center p-4" v-if="searchQuery">
                <i class="pi pi-search" style="font-size: 3rem; color: var(--surface-400)"></i>
                <p>No banks found matching your search.</p>
              </div>
            </template>
          </DataTable>
        </div>
      </template>
    </Card>
  </main>

  <!-- Add New Bank Dialog -->
  <Dialog
    v-model:visible="addNewBank"
    :draggable="false"
    modal
    :style="{ width: '40rem' }"
    :closable="true"
    closeOnEscape
    header="Add New Bank"
    class="bank-dialog"
  >
    <div class="bank-form">
      <div class="form-field">
        <label for="bankName" class="form-label">Bank Name <span class="required-mark">*</span></label>
        <InputText 
          id="bankName" 
          v-model="bankName" 
          placeholder="Enter bank name" 
          class="form-input w-full" 
          aria-required="true"
        />
      </div>

      <div class="form-field">
        <label for="accountNo" class="form-label">Account Number <span class="required-mark">*</span></label>
        <InputText 
          id="accountNo" 
          v-model="accountNo" 
          placeholder="Enter account number" 
          class="form-input w-full" 
          aria-required="true"
        />
      </div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <Button
          label="Cancel"
          icon="pi pi-times"
          outlined
          @click="(addNewBank = false), (accountNo = ''), (bankName = '')"
          class="p-button-secondary"
        />
        <Button 
          label="Add Bank" 
          icon="pi pi-check" 
          @click="addBank" 
          :disabled="!bankName || !accountNo"
          class="p-button-primary"
        />
      </div>
    </template>
  </Dialog>

  <!-- Transaction Dialog -->
  <Dialog
    v-model:visible="viewDialog"
    :draggable="false"
    modal
    :style="{ width: '40rem' }"
    :closable="true"
    closeOnEscape
    :header="`${action} - ${selectedBank?.bankName}`"
    class="transaction-dialog"
  >
    <div class="transaction-info" v-if="selectedBank">
      <div class="bank-summary">
        <h3 class="bank-name">{{ selectedBank.bankName }}</h3>
        <div class="bank-details">
          <div class="detail-item">
            <span class="detail-label">Account Number:</span>
            <span class="detail-value">{{ selectedBank.accountNumber }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Current Balance:</span>
            <span class="detail-value balance">{{ ConversionUtility.toStringRupeesConversion(selectedBank.balance) }}</span>
          </div>
        </div>
      </div>

      <Divider />

      <div class="transaction-form">
        <div class="form-field">
          <label for="transDate" class="form-label">Transaction Date <span class="required-mark">*</span></label>
          <DatePicker
            v-model="transDate"
            id="transDate"
            class="w-full"
            :max-date="new Date()"
            dateFormat="dd/mm/yy"
            showIcon
            placeholder="Select date"
            aria-required="true"
          />
        </div>
        
        <div class="form-field">
          <label :for="action === 'Deposit' ? 'depositAmount' : 'withdrawAmount'" class="form-label">
            {{ action }} Amount <span class="required-mark">*</span>
          </label>
          <InputNumber
            :id="action === 'Deposit' ? 'depositAmount' : 'withdrawAmount'"
            v-model="amount"
            locale="en-IN"
            mode="currency"
            currency="INR"
            :maxFractionDigits="0"
            class="w-full"
            placeholder="Enter amount"
            :min="0"
            :max="action === 'Withdraw' ? maxWithdrawAmount : undefined"
            aria-required="true"
          />
          <small v-if="action === 'Withdraw'" class="helper-text">
            Maximum amount: {{ ConversionUtility.toStringRupeesConversion(selectedBank.balance) }}
          </small>
        </div>

        <div class="form-field">
          <label for="description" class="form-label">Description</label>
          <InputText 
            id="description" 
            v-model="description" 
            class="w-full" 
            placeholder="Enter transaction description (optional)"
          />
        </div>

        <div v-if="action === 'Withdraw' && amount > 0" class="transaction-preview">
          <h4>Transaction Preview</h4>
          <div class="preview-details">
            <div class="preview-item">
              <span>Current Balance:</span>
              <span>{{ ConversionUtility.toStringRupeesConversion(selectedBank.balance) }}</span>
            </div>
            <div class="preview-item">
              <span>Withdrawal Amount:</span>
              <span class="text-red-500">-{{ ConversionUtility.toStringRupees(amount) }}</span>
            </div>
            <div class="preview-item final-balance">
              <span>Final Balance:</span>
              <span>{{ ConversionUtility.toStringRupees(maxWithdrawAmount - amount) }}</span>
            </div>
          </div>
        </div>

        <div v-if="action === 'Deposit' && amount > 0" class="transaction-preview">
          <h4>Transaction Preview</h4>
          <div class="preview-details">
            <div class="preview-item">
              <span>Current Balance:</span>
              <span>{{ ConversionUtility.toStringRupeesConversion(selectedBank.balance) }}</span>
            </div>
            <div class="preview-item">
              <span>Deposit Amount:</span>
              <span class="text-green-500">+{{ ConversionUtility.toStringRupees(amount) }}</span>
            </div>
            <div class="preview-item final-balance">
              <span>Final Balance:</span>
              <span>{{ ConversionUtility.toStringRupees(maxWithdrawAmount + amount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <template #footer>
      <div class="dialog-footer">
        <Button
          label="Cancel"
          icon="pi pi-times"
          outlined
          @click="(viewDialog = false), (action = ''), (transDate = new Date()), (amount = 0), (description = '')"
          class="p-button-secondary"
        />
        <Button 
          :label="action" 
          :icon="action === 'Deposit' ? 'pi pi-plus' : 'pi pi-minus'" 
          @click="performAction" 
          :disabled="!transDate || amount <= 0 || (action === 'Withdraw' && !isWithdrawValid)"
          :class="action === 'Deposit' ? 'p-button-success' : 'p-button-danger'"
        />
      </div>
    </template>
  </Dialog>

  <!-- Add Bank Floating Button -->
  <Button
    icon="pi pi-plus"
    label="Add Bank"
    class="add-bank-button p-button-raised"
    @click="handleAddClick"
    aria-label="Add new bank"
  />

  <div v-if="isLoading">
    <LoadingComponent
      text="Please wait while we process the request!"
      :dialogVisiblity="isLoading"
    />
  </div>
  <ConfirmDialog :draggable="false" :closable="false" />
</template>

<style scoped>
.external-banks-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.5rem 0;
}

.search-container {
  display: flex;
  align-items: center;
}

.search-container .p-input-icon-left {
  width: 100%;
}

.search-container input {
  width: 280px;
  border-radius: 20px;
  transition: all 0.3s ease;
}

.search-container input:focus {
  width: 320px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.data-table-container {
  margin-top: 1rem;
}

.external-banks-table {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  overflow: hidden;
}

.bank-name-cell {
  display: flex;
  align-items: center;
  font-weight: 600;
}

.account-number {
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

.balance-amount {
  font-weight: 600;
}

.date-cell {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-start;
  align-items: center;
}

.action-buttons button {
  font-weight: 600;
}

.deposit-btn {
  background-color: var(--green-600);
  border-color: var(--green-600);
  color: white;
}

.deposit-btn:hover {
  background-color: var(--green-700) !important;
  border-color: var(--green-700) !important;
}

.withdraw-btn {
  background-color: var(--red-600);
  border-color: var(--red-600);
  color: white;
}

.withdraw-btn:hover {
  background-color: var(--red-700) !important;
  border-color: var(--red-700) !important;
}

/* Add Bank Button */
.add-bank-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.add-bank-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Dialog Styling */
.bank-dialog, .transaction-dialog {
  border-radius: 12px;
}

.bank-form, .transaction-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--text-color-secondary);
}

.form-input {
  padding: 0.75rem;
  border-radius: 8px;
}

.required-mark {
  color: var(--red-500);
}

.helper-text {
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
}

/* Transaction Dialog Specific */
.bank-summary {
  background-color: var(--surface-50);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.bank-name {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.bank-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
}

.detail-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.detail-value {
  font-weight: 600;
}

.detail-value.balance {
  color: var(--primary-color);
}

.transaction-preview {
  background-color: var(--surface-50);
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.transaction-preview h4 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-color);
}

.preview-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.preview-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.95rem;
}

.final-balance {
  font-weight: 600;
  border-top: 1px solid var(--surface-200);
  padding-top: 0.5rem;
  margin-top: 0.25rem;
}

/* Responsive Styles */
@media screen and (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-container input {
    width: 100%;
  }
  
  .search-container input:focus {
    width: 100%;
  }
  
  .add-bank-button {
    bottom: 1.5rem;
    right: 1.5rem;
  }
  
  .action-buttons {
    flex-direction: row;
  }
}
</style>
